/**
 * Helpers puros de la solicitud pública para ser partner (RES-94).
 *
 * El backend (`POST /partners/applications`) valida todo y es la fuente de verdad; acá se
 * adelantan las validaciones obvias para marcar el campo sin ir al servidor.
 */

export const APPLY_PROGRAMS = ['influencer', 'cm'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INSTAGRAM_RE = /^[A-Za-z0-9._]{1,30}$/;

export const initialApplicationForm = () => ({
  program: '',
  name: '',
  email: '',
  phone: '',
  instagram: '',
  locationsManaged: '',
  resaleInterest: false,
  message: '',
  website: '', // campo trampa: un humano no lo ve, se manda tal cual
});

const clean = (value) => String(value ?? '').trim();

/**
 * Payload para `createPartnerApplication`: solo los campos del programa elegido.
 * @param {object} form formulario
 * @returns {object} payload
 */
export function buildApplicationPayload(form) {
  const payload = {
    program: form.program,
    name: clean(form.name),
    email: clean(form.email).toLowerCase(),
    website: form.website ?? '',
  };
  const phone = clean(form.phone);
  if (phone) payload.phone = phone;
  const message = clean(form.message);
  if (message) payload.message = message;
  const instagram = clean(form.instagram).replace(/^@/, '');
  if (form.program === 'influencer') {
    payload.instagram = instagram;
  } else if (form.program === 'cm') {
    if (instagram) payload.instagram = instagram;
    const locations = clean(form.locationsManaged);
    payload.locationsManaged = locations === '' ? null : Number(locations);
    payload.resaleInterest = form.resaleInterest === true;
  }
  return payload;
}

/**
 * Primer campo inválido o null.
 * @param {object} form formulario
 * @returns {string|null} campo
 */
export function validateApplicationForm(form) {
  const payload = buildApplicationPayload(form);
  if (!APPLY_PROGRAMS.includes(payload.program)) return 'program';
  if (!payload.name || payload.name.length > 120) return 'name';
  if (!EMAIL_RE.test(payload.email)) return 'email';
  if (payload.phone && payload.phone.length > 40) return 'phone';
  if (payload.program === 'influencer' && !payload.instagram) return 'instagram';
  if (payload.instagram && !INSTAGRAM_RE.test(payload.instagram)) return 'instagram';
  if (payload.program === 'cm') {
    const n = payload.locationsManaged;
    if (!Number.isInteger(n) || n < 0 || n > 10000) return 'locationsManaged';
  }
  if (payload.message && payload.message.length > 1000) return 'message';
  return null;
}

/**
 * Traduce el error del backend a un estado de UI.
 * @param {*} err error de axios
 * @returns {{type: 'field', field: string, message?: string}|{type: 'pending'}|{type: 'generic'}}
 */
export function getApplicationErrorState(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 409 && data.code === 'APPLICATION_PENDING') return { type: 'pending' };
  if (status === 400 && data.code === 'INVALID_APPLICATION' && data.field) {
    return { type: 'field', field: data.field, message: data.message };
  }
  return { type: 'generic' };
}
